/**
 * Posts Service
 *
 * DB-first data layer for Posts Phase 1 (broadcast feed). The feed read goes
 * through the `get_post_feed` RPC (RLS-enforced audience resolution, keyset
 * pagination); post creation and likes are direct table writes — the policies
 * from migration 019 enforce visibility and ownership.
 *
 * Read functions THROW on Supabase error (cached-read contract: a failed fetch
 * must never look like an empty result, or it would overwrite good cached data).
 *
 * This is permanent code — designed to port directly to React Native.
 */

import { supabase } from '../supabase';
import { SOURCE_BRANCH } from '../../utils/deployInfo';
import { uploadCompressedImage } from '../storage/chatImageUpload';

/** Feed page size — mirrors get_post_feed's default; RPC clamps to [1, 50]. */
export const FEED_PAGE_SIZE = 20;

/**
 * URL detection shared with the render layer (PostCard linkifies the same span).
 * Three forms: scheme URLs, www.-prefixed, and bare domains on a common-TLD
 * allowlist (user-decided 2026-07-13: "google.com" should link) — the
 * allowlist keeps sentence fragments like "etc.Great" from linkifying.
 */
const URL_REGEX =
  /(https?:\/\/[^\s]+|www\.[^\s]+|[a-z0-9][a-z0-9-]*(\.[a-z0-9-]+)*\.(com|net|org|io|app|co|dev|me|ly|tv|fm|so|ai|xyz)(\/[^\s]*)?)/gi;

/**
 * Find the first URL-looking span in text. Returns { index, raw } with
 * trailing punctuation stripped from raw (so "foo.com/x!" links cleanly and
 * the "!" renders as plain text). Skips matches glued to a preceding @/word
 * character — "mark@gmail.com" must not linkify its domain half.
 *
 * @param {string} text
 * @returns {{index: number, raw: string}|null}
 */
export const findFirstUrl = (text) => {
  if (!text) return null;

  for (const match of text.matchAll(URL_REGEX)) {
    const prev = match.index > 0 ? text[match.index - 1] : '';
    if (prev && /[@\w.-]/.test(prev)) continue;

    const raw = match[0].replace(/[.,;:!?)\]]+$/, '');
    if (!raw) continue;

    return { index: match.index, raw };
  }

  return null;
};

/**
 * Extract the first URL from post text. Posts have no dedicated URL input —
 * a URL pasted in the text is detected at post time and stored in posts.url
 * (decided 2026-07-13). Scheme-less matches get https:// prepended.
 *
 * @param {string} text
 * @returns {string|null} Normalized URL or null if none found
 */
export const extractFirstUrl = (text) => {
  const found = findFirstUrl(text);
  if (!found) return null;

  return /^https?:\/\//i.test(found.raw) ? found.raw : `https://${found.raw}`;
};

/**
 * Fetch one feed page via the get_post_feed RPC. Caller identity is derived
 * from auth.uid() inside the RPC — nothing spoofable is sent. Keyset cursor:
 * pass BOTH beforeCreatedAt and beforeId (from the last row of the previous
 * page) or neither (page 1). Throws on Supabase error.
 *
 * @param {Object} [opts]
 * @param {number} [opts.limit]
 * @param {string} [opts.beforeCreatedAt] - created_at of the last row seen
 * @param {string} [opts.beforeId] - id of the last row seen
 * @returns {Promise<Array>} Feed rows: { id, author_id, author_name,
 *   author_photo_url, text, photo_url, url, location_snapshot, created_at,
 *   like_count, viewer_liked }
 */
export const fetchFeedPage = async ({ limit = FEED_PAGE_SIZE, beforeCreatedAt, beforeId } = {}) => {
  const { data, error } = await supabase.rpc('get_post_feed', {
    p_limit: limit,
    p_before_created_at: beforeCreatedAt ?? null,
    p_before_id: beforeId ?? null,
  });

  if (error) {
    throw new Error(`[POSTS] fetchFeedPage failed: ${error.message}`);
  }

  return data || [];
};

/**
 * Create a post. Direct insert — the author manage policy enforces identity.
 * Phase 1 is broadcast: no post_audiences rows are written (zero rows =
 * broadcast to connections as of posting time, enforced in the DB).
 * The URL is extracted from the text here (no dedicated URL input).
 *
 * @param {Object} params
 * @param {string} params.authorId - Caller's profile_user_id
 * @param {string} [params.text]
 * @param {string} [params.photoUrl] - Public URL from uploadPostPhoto
 * @param {string} [params.locationSnapshot] - Bare neighborhood (nullable —
 *   location must never block posting)
 * @returns {Promise<Object>} The created post row
 * @throws {Error} If content is empty or the insert fails
 */
export const createPost = async ({ authorId, text, photoUrl, locationSnapshot }) => {
  const trimmedText = text?.trim() || null;
  const url = extractFirstUrl(trimmedText);

  // Mirrors the DB CHECK (posts_has_content): at least one of text/photo/url
  if (!trimmedText && !photoUrl && !url) {
    throw new Error('[POSTS] createPost rejected: post has no content');
  }

  const { data, error } = await supabase
    .from('posts')
    .insert({
      author_id: authorId,
      text: trimmedText,
      photo_url: photoUrl || null,
      url,
      location_snapshot: locationSnapshot || null,
      source_branch: SOURCE_BRANCH,
    })
    .select()
    .single();

  if (error) {
    throw new Error(`[POSTS] createPost failed: ${error.message}`);
  }

  return data;
};

/**
 * Delete a post (author only — manage policy enforces it). Cascades to
 * post_audiences and likes. No Phase 1 UI calls this; the delete UI ships
 * with the detail page in posts-comments-v1.
 *
 * @param {string} postId
 * @returns {Promise<boolean>} Success
 */
export const deletePost = async (postId) => {
  if (!postId) return false;

  const { error } = await supabase.from('posts').delete().eq('id', postId);

  if (error) {
    console.error('[POSTS] deletePost failed:', error.message);
    return false;
  }

  return true;
};

/**
 * Like a post. Direct insert — the INSERT policy enforces own-row +
 * post-visible. The composite PK (post_id, profile_user_id) makes a
 * double-tap race a clean conflict, treated as already-liked.
 *
 * @param {string} postId
 * @param {string} profileUserId - Caller's profile_user_id
 * @returns {Promise<boolean>} Success (including already-liked)
 */
export const likePost = async (postId, profileUserId) => {
  if (!postId || !profileUserId) return false;

  const { error } = await supabase.from('likes').insert({
    post_id: postId,
    profile_user_id: profileUserId,
    source_branch: SOURCE_BRANCH,
  });

  if (error) {
    // 23505 unique_violation: already liked (double-tap race) — not a failure
    if (error.code === '23505') return true;
    console.error('[POSTS] likePost failed:', error.message);
    return false;
  }

  return true;
};

/**
 * Unlike a post. The DELETE policy is own-row only, with no visibility check —
 * a like can always be retracted, even after disconnecting from the author.
 *
 * @param {string} postId
 * @param {string} profileUserId - Caller's profile_user_id
 * @returns {Promise<boolean>} Success
 */
export const unlikePost = async (postId, profileUserId) => {
  if (!postId || !profileUserId) return false;

  const { error } = await supabase
    .from('likes')
    .delete()
    .eq('post_id', postId)
    .eq('profile_user_id', profileUserId);

  if (error) {
    console.error('[POSTS] unlikePost failed:', error.message);
    return false;
  }

  return true;
};

/**
 * Compress and upload a post photo to the post-photos bucket.
 * Feed-appropriate sizing via the chat-images compression ladder (1200px,
 * progressive re-compression) — NOT profile-photos' 200×200 avatar target.
 * Throws on failure — no base64 fallback (a post must never be created with
 * a dangling photo_url).
 *
 * @param {File} file - Image file from file input
 * @param {string} profileUserId - Author's profile_user_id (path prefix)
 * @returns {Promise<string>} Public URL of the uploaded image
 * @throws {Error} If compression or upload fails
 */
export const uploadPostPhoto = (file, profileUserId) =>
  uploadCompressedImage('post-photos', profileUserId, file, '[POSTS]');
